// Grooming photo diary. Two experiences in one component:
//   • Horizontal timeline strip of every session that has at least one photo
//     (before or after). Click a thumbnail to open the lightbox.
//   • Lightbox with before/after side-by-side for the picked session, plus
//     a "compare with…" dropdown that overlays any other dated session on
//     the right for direct comparison.
import { useCallback, useEffect, useMemo, useState } from "react";
import { format, parseISO } from "date-fns";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { SectionTitle } from "@/components/app/Bits";
import { Button } from "@/components/ui/button";
import { Images, X, ChevronLeft, ChevronRight } from "lucide-react";

// Pull the primary photo for a session — prefer the "after" shot since that's
// usually what you show off.
function primaryPhoto(session) {
  return session.after_photo_url || session.before_photo_url || null;
}

function sessionsWithPhotos(list) {
  return (list || []).filter((s) => s.before_photo_url || s.after_photo_url);
}

export default function GroomingPhotoDiary({ dog }) {
  const [sessions, setSessions] = useState([]);
  const [openIndex, setOpenIndex] = useState(null);         // index into `withPhotos`
  const [compareId, setCompareId] = useState("");           // session.id being overlaid

  const load = useCallback(async () => {
    if (!dog?.id) return;
    try {
      const { data } = await api.get("/grooming/sessions", { params: { dog_id: dog.id } });
      setSessions(data.items || []);
    } catch { setSessions([]); }
  }, [dog?.id]);

  useEffect(() => { load(); }, [load]);

  const withPhotos = useMemo(() => sessionsWithPhotos(sessions), [sessions]);

  const activeSession = openIndex !== null ? withPhotos[openIndex] : null;
  const compareSession = useMemo(
    () => (compareId ? sessions.find((s) => s.id === compareId) : null),
    [compareId, sessions],
  );

  const openAt = (i) => { setOpenIndex(i); setCompareId(""); };
  const close = () => setOpenIndex(null);
  const prev = () => setOpenIndex((i) => (i > 0 ? i - 1 : i));
  const next = () => setOpenIndex((i) => (i < withPhotos.length - 1 ? i + 1 : i));

  if (withPhotos.length === 0) {
    return null;    // No photos yet — nothing to render.
  }

  return (
    <div className="space-y-4" data-testid="grooming-photo-diary">
      <SectionTitle overline="Timeline" title="Photo diary" />
      <p className="text-sm text-muted-foreground max-w-2xl -mt-2">
        Every session with a photo lives here in date order. Tap any card to
        open the lightbox and compare with another visit.
      </p>

      <Card className="border-border shadow-none rounded-lg">
        <CardContent className="p-4 sm:p-5">
          <ul
            className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1"
            data-testid="photo-diary-timeline"
          >
            {withPhotos.map((s, i) => {
              const src = primaryPhoto(s);
              return (
                <li key={s.id} className="shrink-0 w-28" data-testid={`photo-diary-item-${s.id}`}>
                  <button
                    type="button"
                    onClick={() => openAt(i)}
                    className="block w-full text-left group"
                  >
                    <div className="relative aspect-square rounded-md overflow-hidden border border-border">
                      <img
                        src={src}
                        alt={`Grooming session ${s.session_date}`}
                        className="w-full h-full object-cover group-hover:opacity-90"
                      />
                      {s.before_photo_url && s.after_photo_url ? (
                        <span className="absolute top-1 right-1 text-[9px] uppercase tracking-wider text-white bg-black/60 rounded px-1 py-0.5">
                          B/A
                        </span>
                      ) : null}
                    </div>
                    <p className="mt-1.5 text-[11px] text-muted-foreground truncate">
                      {format(parseISO(s.session_date), "MMM d, yyyy")}
                    </p>
                    {s.trim_type ? (
                      <p className="text-[11px] font-medium truncate">{s.trim_type}</p>
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        </CardContent>
      </Card>

      {activeSession ? (
        <PhotoLightbox
          session={activeSession}
          compareSession={compareSession}
          allSessions={withPhotos.filter((s) => s.id !== activeSession.id)}
          onCompareChange={setCompareId}
          onClose={close}
          onPrev={openIndex > 0 ? prev : null}
          onNext={openIndex < withPhotos.length - 1 ? next : null}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Lightbox — before/after for the active session, optional side-by-side
// overlay of another chosen session.
// ---------------------------------------------------------------------------
function PhotoLightbox({ session, compareSession, allSessions, onCompareChange, onClose, onPrev, onNext }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft" && onPrev) onPrev();
      if (e.key === "ArrowRight" && onNext) onNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, onPrev, onNext]);

  const showCompare = !!compareSession;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 flex flex-col"
      data-testid="photo-lightbox"
      role="dialog"
      aria-modal="true"
    >
      {/* Top bar */}
      <div className="flex items-center justify-between gap-2 p-3 sm:p-4 text-white">
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-[0.18em] text-white/60">
            {format(parseISO(session.session_date), "MMMM d, yyyy")}
          </p>
          <p className="font-display font-bold text-lg truncate">
            {session.trim_type || "Grooming session"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={compareSession?.id || ""}
            onChange={(e) => onCompareChange(e.target.value)}
            className="text-xs bg-black/60 border border-white/20 text-white rounded-md px-2 py-1.5"
            data-testid="lightbox-compare-select"
          >
            <option value="">Compare with…</option>
            {allSessions.map((s) => (
              <option key={s.id} value={s.id}>
                {format(parseISO(s.session_date), "MMM d, yyyy")}{s.trim_type ? ` — ${s.trim_type}` : ""}
              </option>
            ))}
          </select>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onClose}
            className="border-white/30 text-white bg-transparent hover:bg-white/10"
            data-testid="lightbox-close"
          >
            <X size={14} className="mr-1" /> Close
          </Button>
        </div>
      </div>

      {/* Images */}
      <div className="flex-1 flex items-stretch justify-between gap-2 sm:gap-4 px-2 sm:px-4 pb-4 min-h-0">
        {onPrev ? (
          <button
            type="button"
            onClick={onPrev}
            className="text-white/70 hover:text-white p-2"
            aria-label="Previous session"
            data-testid="lightbox-prev"
          >
            <ChevronLeft size={28} />
          </button>
        ) : <div className="w-10" />}

        <div className={`flex-1 grid gap-2 sm:gap-4 min-h-0 ${showCompare ? "grid-cols-2 md:grid-cols-4" : "grid-cols-2"}`}>
          <PhotoTile url={session.before_photo_url} label="Before" date={session.session_date} />
          <PhotoTile url={session.after_photo_url} label="After" date={session.session_date} />
          {showCompare ? (
            <>
              <PhotoTile url={compareSession.before_photo_url} label="Before" date={compareSession.session_date} />
              <PhotoTile url={compareSession.after_photo_url} label="After" date={compareSession.session_date} />
            </>
          ) : null}
        </div>

        {onNext ? (
          <button
            type="button"
            onClick={onNext}
            className="text-white/70 hover:text-white p-2"
            aria-label="Next session"
            data-testid="lightbox-next"
          >
            <ChevronRight size={28} />
          </button>
        ) : <div className="w-10" />}
      </div>
    </div>
  );
}

function PhotoTile({ url, label, date }) {
  return (
    <div className="relative flex flex-col items-center justify-center bg-black/40 rounded-md overflow-hidden min-h-0">
      {url ? (
        <img
          src={url}
          alt={`${label} — ${date}`}
          className="max-h-full max-w-full object-contain"
        />
      ) : (
        <div className="flex flex-col items-center gap-2 text-white/40 text-xs">
          <Images size={22} />
          <span>No {label.toLowerCase()} photo</span>
        </div>
      )}
      <span className="absolute bottom-1 left-1 text-[10px] uppercase tracking-wider text-white bg-black/60 rounded px-1.5 py-0.5">
        {label} · {format(parseISO(date), "MMM d")}
      </span>
    </div>
  );
}
